import { useEffect, useMemo } from 'react';

import { mapAnalysisStats } from '@/shared/api/mappers/analysis';
import type { ProductItemCondition, ProductStatus } from '@/shared/api/types/generated';
import { useGetAnalysisStatsQuery, useGetAvailableItemConditionsQuery } from '@/shared/hooks/queries/generated';

import type { AnalysisStatisticsData } from '../types';

interface UseAnalysisStatisticsParams {
  analysisId: string | undefined;
  itemCondition?: ProductItemCondition | null;
  onAvailableConditionsLoaded?: (conditions: ProductItemCondition[], defaultCondition: ProductItemCondition) => void;
}

interface UseAnalysisStatisticsReturn {
  data: AnalysisStatisticsData | undefined;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  availableConditions: ProductItemCondition[];
  availableConditionsLoading: boolean;
  defaultCondition: ProductItemCondition | null;
}

export const useAnalysisStatistics = ({
  analysisId,
  itemCondition,
  onAvailableConditionsLoaded,
}: UseAnalysisStatisticsParams): UseAnalysisStatisticsReturn => {
  // Récupération des conditions disponibles
  const {
    data: availableConditionsData,
    isLoading: availableConditionsLoading,
    error: availableConditionsError,
  } = useGetAvailableItemConditionsQuery(
    {
      analysisId: analysisId ?? '',
      status: 'sold' as ProductStatus,
    },
    {
      enabled: !!analysisId,
    },
  );

  // Extraction des conditions disponibles et détermination de la condition par défaut
  const { availableConditions, defaultCondition } = useMemo(() => {
    if (!availableConditionsData?.getAvailableItemConditions) {
      return { availableConditions: [], defaultCondition: null };
    }

    const conditions = availableConditionsData.getAvailableItemConditions.map(
      (item) => item.itemCondition,
    ) as ProductItemCondition[];

    return { availableConditions: conditions, defaultCondition: null };
  }, [availableConditionsData]);

  // Notification du parent quand les conditions sont chargées
  useEffect(() => {
    if (defaultCondition && onAvailableConditionsLoaded) {
      onAvailableConditionsLoaded(availableConditions, defaultCondition);
    }
  }, [defaultCondition, availableConditions, onAvailableConditionsLoaded]);

  // Récupération des statistiques
  const {
    data,
    isLoading: loading,
    error,
    refetch,
  } = useGetAnalysisStatsQuery(
    {
      analysisId: analysisId ?? '',
      itemCondition: itemCondition ?? null,
    },
    {
      enabled: !!analysisId,
    },
  );

  const mappedData = data?.getAnalysisStats ? mapAnalysisStats(data.getAnalysisStats) : undefined;

  const combinedError = error || availableConditionsError;

  return {
    data: mappedData,
    loading,
    error: combinedError ? 'Erreur lors de la récupération des statistiques' : null,
    refetch: () => {
      refetch();
    },
    availableConditions,
    availableConditionsLoading,
    defaultCondition,
  };
};
